import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { finalize } from 'rxjs';
import { LoginPayload, LoginResponse } from '../../../core/models/user.model';
import { ApiService } from '../../../core/services/api.service';
import { ApiAuthService } from '../../../core/services/api/auth/api-auth.service';
import { MenuPermissionService } from '../../../core/services/menu-permission.service';
import {
  PssAlertModalComponent,
  PssAlertModalVariant,
} from '../../../tools/template/pss/pss-alert-modal/pss-alert-modal.component';
import { PssLoadingComponent } from '../../../tools/template/pss/pss-loading/pss-loading.component';
import { consumeAdminSessionEndReason } from '../../../core/interceptors/auth-session.interceptor';

type LoginViewMode = 'login' | 'first' | 'change';

const MUST_CHANGE_COOKIE = 'must_change_password';
const CONCURRENT_REJECT = 'บัญชีนี้กำลังใช้งานอยู่ กรุณา Logout จากอุปกรณ์เดิมก่อน';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PssAlertModalComponent, PssLoadingComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {
  mode: LoginViewMode = 'login';
  username = '';
  password = '';
  newPassword = '';
  confirmPassword = '';
  private currentPasswordForChange = '';
  appVersion = '0.0.0';
  readonly currentYear = new Date().getFullYear();

  showPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  isLoading = false;

  isAlertOpen = false;
  alertTitle = '';
  alertMessage = '';
  alertVariant: PssAlertModalVariant = 'info';

  private readonly apiService = inject(ApiService);
  private readonly apiAuth = inject(ApiAuthService);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);
  private readonly http = inject(HttpClient);
  private readonly permissionService = inject(MenuPermissionService);

  get heading(): string {
    if (this.mode === 'first') {
      return 'ตั้งรหัสผ่านครั้งแรก';
    }
    if (this.mode === 'change') {
      return 'เปลี่ยนรหัสผ่าน';
    }
    return 'ยินดีต้อนรับกลับ';
  }

  get subheading(): string {
    if (this.mode === 'first') {
      return 'บัญชีนี้ยังไม่มีรหัส City Login กรุณาตั้งรหัสผ่านเพื่อเข้าใช้งาน';
    }
    if (this.mode === 'change') {
      return 'ต้องเปลี่ยนรหัสผ่านก่อนเข้าใช้งานระบบ';
    }
    return 'กรุณายืนยันตัวตนเพื่อเข้าสู่ระบบ Admin';
  }

  get needsCurrentPassword(): boolean {
    return this.mode === 'change' && !this.currentPasswordForChange;
  }

  ngOnInit(): void {
    this.checkAppVersion();
    if (consumeAdminSessionEndReason() === 'session_expired') {
      this.openAlert(
        'warning',
        'เซสชันหมดอายุ',
        'มีการเข้าสู่ระบบจากอุปกรณ์อื่น หรือเซสชันถูกปิดเนื่องจากไม่มีการใช้งาน กรุณาเข้าสู่ระบบใหม่',
      );
    }
    if (this.cookieService.get(MUST_CHANGE_COOKIE) === '1' && this.cookieService.get('token')) {
      this.mode = 'change';
      this.username = this.cookieService.get('username') || this.username;
    }
  }

  checkAppVersion(): void {
    this.http
      .get<{ version: string }>('./json/version.json', {
        headers: { 'Cache-Control': 'no-cache' },
      })
      .subscribe({
        next: (response) => {
          this.appVersion = (response?.version || '').trim() || '—';
        },
        error: () => {
          this.appVersion = '—';
        },
      });
  }

  togglePassword(): void {
    if (this.isLoading) return;
    this.showPassword = !this.showPassword;
  }

  toggleNewPassword(): void {
    if (this.isLoading) return;
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPassword(): void {
    if (this.isLoading) return;
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  openAlert(variant: PssAlertModalVariant, title: string, message: string): void {
    this.alertVariant = variant;
    this.alertTitle = title;
    this.alertMessage = message;
    this.isAlertOpen = true;
  }

  closeAlert(): void {
    this.isAlertOpen = false;
  }

  onSubmit(): void {
    if (this.mode === 'first') {
      this.submitFirstPassword();
      return;
    }
    if (this.mode === 'change') {
      this.submitChangePassword();
      return;
    }
    this.login();
  }

  backToLogin(): void {
    if (this.isLoading) return;
    if (this.mode === 'change' && this.cookieService.get('token')) {
      this.isLoading = true;
      this.apiAuth.logout().pipe(finalize(() => this.resetToLoginForm())).subscribe({
        next: () => undefined,
        error: () => undefined,
      });
      return;
    }
    this.resetToLoginForm();
  }

  login(): void {
    if (this.isLoading) return;

    const username = this.username.trim();
    const password = this.password;

    if (!username) {
      this.openAlert('warning', 'ข้อมูลไม่ครบ', 'กรุณากรอก Username');
      return;
    }

    const payload: LoginPayload = { username, password };
    this.isLoading = true;
    // เคลียร์ cookie ฝั่ง FE ก่อน login ใหม่ (หลังถูกเตะ / session หมดอายุ)
    this.clearLocalSession();

    this.apiService.logIn(payload).subscribe({
      next: (res: LoginResponse) => {
        if (res.needs_first_password) {
          this.isLoading = false;
          this.username = username;
          this.password = '';
          this.newPassword = '';
          this.confirmPassword = '';
          this.mode = 'first';
          this.openAlert('info', 'ตั้งรหัสผ่าน', 'กรุณาตั้งรหัสผ่านใหม่เพื่อเข้าใช้งานครั้งแรก');
          return;
        }

        if (res.status) {
          this.applySessionCookies(res);
          if (res.must_change_password) {
            this.currentPasswordForChange = password;
            this.newPassword = '';
            this.confirmPassword = '';
            this.password = '';
            this.mode = 'change';
            this.isLoading = false;
            this.openAlert('warning', 'ต้องเปลี่ยนรหัสผ่าน', 'กรุณาตั้งรหัสผ่านใหม่ก่อนเข้าใช้งาน');
            return;
          }
          this.enterApp();
          return;
        }

        this.isLoading = false;
        this.openAlert('error', 'เข้าสู่ระบบไม่สำเร็จ', this.loginFailMessage(res));
      },
      error: (err) => {
        console.error('❌ ไม่สำเร็จ:', err);
        this.isLoading = false;
        this.openAlert(
          'error',
          'เชื่อมต่อไม่สำเร็จ',
          'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่ภายหลัง',
        );
      },
    });
  }

  private submitFirstPassword(): void {
    if (this.isLoading) return;
    const username = this.username.trim();
    if (!this.validateNewPasswordPair()) {
      return;
    }

    this.isLoading = true;
    this.apiAuth.setFirstPassword({ username, password: this.newPassword }).subscribe({
      next: (res: LoginResponse) => {
        if (res.status) {
          this.applySessionCookies(res);
          if (res.must_change_password) {
            this.currentPasswordForChange = this.newPassword;
            this.newPassword = '';
            this.confirmPassword = '';
            this.mode = 'change';
            this.isLoading = false;
            return;
          }
          this.enterApp();
          return;
        }
        this.isLoading = false;
        this.openAlert('error', 'ตั้งรหัสผ่านไม่สำเร็จ', this.loginFailMessage(res));
      },
      error: () => {
        this.isLoading = false;
        this.openAlert('error', 'เชื่อมต่อไม่สำเร็จ', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่ภายหลัง');
      },
    });
  }

  private submitChangePassword(): void {
    if (this.isLoading) return;
    if (!this.validateNewPasswordPair()) {
      return;
    }

    const currentPassword = this.currentPasswordForChange || this.password;
    if (!currentPassword) {
      this.openAlert('warning', 'ข้อมูลไม่ครบ', 'กรุณากรอกรหัสผ่านปัจจุบัน');
      return;
    }

    this.isLoading = true;
    this.apiAuth.changeMyPassword({
      currentPassword,
      newPassword: this.newPassword,
    }).subscribe({
      next: (res) => {
        if (!res?.isSuccess) {
          this.isLoading = false;
          this.openAlert('error', 'เปลี่ยนรหัสผ่านไม่สำเร็จ', res?.message || 'กรุณาลองใหม่');
          return;
        }
        this.cookieService.delete(MUST_CHANGE_COOKIE, '/');
        this.enterApp();
      },
      error: () => {
        this.isLoading = false;
        this.openAlert('error', 'เชื่อมต่อไม่สำเร็จ', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ กรุณาลองใหม่ภายหลัง');
      },
    });
  }

  private validateNewPasswordPair(): boolean {
    if (!this.newPassword || !this.confirmPassword) {
      this.openAlert('warning', 'ข้อมูลไม่ครบ', 'กรุณากรอกรหัสผ่านใหม่และยืนยันรหัสผ่าน');
      return false;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.openAlert('warning', 'รหัสไม่ตรงกัน', 'รหัสผ่านใหม่และยืนยันรหัสผ่านไม่ตรงกัน');
      return false;
    }
    return true;
  }

  private applySessionCookies(res: LoginResponse): void {
    this.cookieService.set('user_id', res.id.toString(), undefined, '/');
    this.cookieService.set('username', (res.username || this.username).toString(), undefined, '/');
    this.cookieService.set('name', (res.name || '').toString(), undefined, '/');
    this.cookieService.set('company_id', res.company_id.toString(), undefined, '/');
    this.cookieService.set('token', (res.access_token || '').toString(), undefined, '/');
    if (res.must_change_password) {
      this.cookieService.set(MUST_CHANGE_COOKIE, '1', undefined, '/');
    } else {
      this.cookieService.delete(MUST_CHANGE_COOKIE, '/');
    }
  }

  private enterApp(): void {
    this.permissionService.resetSession();
    this.permissionService.loadUserMenus().subscribe({
      next: () => {
        const targetRoute = this.permissionService.getDefaultLandingRoute();
        this.router.navigateByUrl(targetRoute, { replaceUrl: true });
      },
      error: () => {
        this.isLoading = false;
        this.openAlert('error', 'เกิดข้อผิดพลาด', 'ไม่สามารถโหลดเมนูผู้ใช้ได้');
      },
    });
  }

  private loginFailMessage(res: LoginResponse): string {
    const hint = (res.username || '').trim();
    if (hint === CONCURRENT_REJECT) {
      return CONCURRENT_REJECT;
    }
    if (hint && hint.toLowerCase() !== this.username.trim().toLowerCase()) {
      return hint;
    }
    return 'Username หรือ Password ผิด กรุณาลองใหม่!';
  }

  private resetToLoginForm(): void {
    this.mode = 'login';
    this.password = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.currentPasswordForChange = '';
    this.showPassword = false;
    this.showNewPassword = false;
    this.showConfirmPassword = false;
    this.isLoading = false;
    this.clearLocalSession();
  }

  private clearLocalSession(): void {
    const path = '/';
    const keys = ['token', 'user_id', 'username', 'name', 'company_id', MUST_CHANGE_COOKIE];
    for (const key of keys) {
      this.cookieService.delete(key, path);
    }
  }
}
